import React, { useEffect, useState } from "react";
import _ from "lodash/fp";
import { Dialog, GridGenerator, Loading } from "@actinc/dls/components";
import { Box, Button, DialogActions, DialogContent } from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { useClient, useMutation } from "urql";

import { ControlledFormInput, DataGrid } from "../../../components";

const AdminProgramKeywordsDialog = ({ closeDialog, programID }) => {
  const client = useClient();
  const [, mutateProgram] = useMutation(updateProgramMutation);

  const [action, setAction] = useState("create");
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);

  const { control, getValues, reset } = useForm({
    defaultValues: { ...defaultValues },
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { isSubmitting, isValid } = useFormState({ control });

  useEffect(() => {
    async function getProgram() {
      const response = await client
        .query(getProgramQuery, { id: programID })
        .toPromise();

      setKeywords(
        response.data?.programByID?.keywords.map(({ description, value }) => ({
          description,
          value,
        })) || []
      );
      setLoading(false);
    }

    getProgram();
  }, [programID]);

  const deleteKeyword = (row) => () => {
    setKeywords(
      keywords.filter((el) => {
        return el.value !== row.value;
      })
    );
  };

  const editKeyword = (row) => () => {
    reset({ ...row });
    setAction("update");
  };

  const onSubmit = (action) => () => {
    const keyword = getValues();
    const index = keywords.findIndex(({ value }) => keyword.value === value);
    switch (action) {
      case "create":
        if (index > -1) return;
        setKeywords([...keywords, keyword]);
        break;
      case "update":
        if (index === -1) return;
        setKeywords(
          keywords.map((k, i) =>
            i === index ? { ...k, description: keyword.description } : { ...k }
          )
        );
        setAction("create");
        break;
    }
    reset({ ...defaultValues });
  };

  const saveChanges = async () => {
    await mutateProgram({
      data: { keywords },
      id: programID,
    });
    closeDialog();
  };

  const columns = [
    {
      field: "value",
      flex: 1,
      headerName: "Keyword",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "description",
      flex: 2,
      headerName: "Description",
      type: "string",
    },
  ];
  const dataGridProps = {
    actions: [
      {
        label: "Edit",
        onClick: editKeyword,
      },
      {
        label: "Delete",
        onClick: deleteKeyword,
      },
    ],
    columns,
    getRowId: (row) => row.value,
    hideFooter: true,
    rows: keywords,
    sortModel: [
      {
        field: "value",
        sort: "asc",
      },
    ],
  };
  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "md",
    onClose: closeDialog,
    open: true,
    scroll: "paper",
    title: `Manage Keywords`,
  };

  return (
    <Dialog {...dialogProps}>
      <DialogContent>
        {loading && (
          <Loading
            circularProgressProps={{
              size: 24,
              thickness: 4.5,
            }}
            title="Loading..."
          />
        )}
        {!loading && (
          <>
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        disabled={action === "update"}
                        fullWidth={true}
                        id="keyword-value"
                        label="Keyword"
                        name="value"
                        rules={{
                          maxLength: 50,
                          required: true,
                        }}
                      />
                    ),
                    key: "keyword-value",
                  },
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id="keyword-description"
                        label="Description"
                        name="description"
                        rules={{
                          maxLength: 50,
                          required: false,
                        }}
                      />
                    ),
                    key: "keyword-description",
                  },
                ],
              ]}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  reset({ ...defaultValues });
                  setAction("create");
                }}
                size="small"
                variant="text"
              >
                Reset
              </Button>
              <Button
                disabled={!isValid}
                onClick={onSubmit(action)}
                size="small"
                variant="outlined"
              >
                {_.capitalize(action)}
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, height: "250px", marginTop: 1 }}>
              <DataGrid {...dataGridProps}></DataGrid>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="text">
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          onClick={saveChanges}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const programFields = `
  id
  keywords {
    value
    description
  }
`;

const getProgramQuery = `
  query GetProgram($id: ID!) {
    programByID(id: $id) { ${programFields} }
  }`;
const updateProgramMutation = `
  mutation UpdateProgram($id: ID!, $data: ProgramInput!) {
    updateProgram(id: $id, data: $data) { ${programFields} }
  }`;

const defaultValues = {
  description: "",
  value: "",
};

export { AdminProgramKeywordsDialog, getProgramQuery, updateProgramMutation };
